import React, { useCallback, useMemo, useState } from "react";
import styled from "styled-components";

import { useSelector } from "services/redux/rootReducer";
import AutoCompleteSelect from "features/shared/components/form/components/autoCompleteSelect";
import Tag from "./tag";
interface Props {
    onChangeHandler: (tags: Array<string>) => void;
    value: string[];
    initialSuggestions?: string[];
    readOnlyMode?: boolean;
}

const TagSelect: React.FC<Props> = ({
    onChangeHandler,
    value: selectedTags,
    initialSuggestions,
    readOnlyMode = false,
}) => {
    const tags = useSelector((state) => state.tags);

    const [inputValue, setInputValue] = useState("");

    const handleAddTag = (tagKey: string) => {
        if (
            !tags
                .map((existingTag) => existingTag.key)
                .find((existingTag) => existingTag === tagKey)
        ) {
            alert("Tag does not exist");
            return;
        }
        if (tagKey && !selectedTags.includes(tagKey)) {
            const updatedSelection = [...selectedTags, tagKey];
            onChangeHandler(updatedSelection);
            setInputValue("");
        }
    };

    const handleRemoveTag = useCallback(
        (tagKey: string) => {
            if (tagKey && selectedTags.includes(tagKey)) {
                const updatedSelection = selectedTags.filter(
                    (selectedTag) => selectedTag !== tagKey
                );
                onChangeHandler(updatedSelection);
            }
        },
        [selectedTags, onChangeHandler]
    );

    const handleTagClick = useCallback(
        (tagKey: string) => {
            const updatedSelection = [
                tagKey,
                ...selectedTags.filter((existingTag) => existingTag !== tagKey),
            ];
            onChangeHandler(updatedSelection);
        },
        [onChangeHandler, selectedTags]
    );

    const displayedTags = useMemo(() => {
        return selectedTags.map((tagKey) => {
            return (
                <Tag
                    readOnlyMode={readOnlyMode}
                    tagKey={tagKey}
                    key={tagKey}
                    onTagClickCallback={handleTagClick}
                    onRemoveTagCallback={handleRemoveTag}
                />
            );
        });
    }, [selectedTags, readOnlyMode, handleTagClick, handleRemoveTag]);

    return (
        <Container>
            <ValueContainer>
                {selectedTags.length ? displayedTags : "(No tag keys added)"}
            </ValueContainer>
            {!readOnlyMode && (
                <AutoCompleteSelect
                    queriedStrings={tags
                        .map((tag) => tag.key)
                        .filter((tagKey) => !selectedTags.includes(tagKey))}
                    onAddHandler={() => handleAddTag(inputValue)}
                    onChangeHandler={(_name, value) => setInputValue(value)}
                    onEnterSubmit={() => handleAddTag(inputValue)}
                    initialSuggestions={initialSuggestions}
                    value={inputValue}
                    name="-"
                />
            )}
        </Container>
    );
};

const Container = styled.div`
    font-size: 1rem;
`;

const ValueContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    margin-bottom: 1rem;
    color: ${(props) => props.theme.colors.neutral[300]};
    user-select: none;
`;

export default TagSelect;
